/**
 * How good somebody's connection is: which way it got there, and how long it takes.
 *
 * Two readings rather than one, because they answer different questions and neither substitutes for
 * the other. The path is where the bytes go, `direct` or through a relay, and it is the half a
 * player can act on: a room that fell back to TURN is a room worth re-hosting from somebody else.
 * The band is what the link actually costs, measured by the heartbeat and smoothed over a window,
 * and it is the half a player feels. Two `direct` players 15 ms and 250 ms away used to read
 * identically, which is the defect this pair exists to fix.
 *
 * A band and not the millisecond figure behind it. See [latencyBand](./latencyBand.ts) for why, and
 * for where the thresholds come from.
 *
 * `weak` outranks the other paths because it is the one that predicts trouble. A pair that fell
 * back to the reliable channel is sending snapshots down a queue that retransmits, which is late
 * frames however direct the route is.
 */

import { bandOf } from '@/net/lobby/latencyBand';
import type { LatencyBand } from '@/net/lobby/latencyBand';
import type { TransportPeer } from '@/net/transportContract';

/** Which way the bytes get there. Derived from what the transport already knows. */
export type LinkPath = 'unknown' | 'direct' | 'relayed' | 'weak';

/** What a roster seat carries, and what a player list renders as two chips. */
export interface LinkQuality {
  readonly path: LinkPath;
  readonly band: LatencyBand;
}

const LABELS: Record<LinkPath, string> = {
  unknown: 'connecting',
  direct: 'direct',
  relayed: 'via relay',
  weak: 'weak link',
};

export interface QualitySignals {
  /** False means this peer's snapshots are riding the reliable channel instead. */
  readonly snapshotChannelReady: boolean;
  /** Null until the first stats sample. True means the pair is going through TURN. */
  readonly relayed: boolean | null;
  /** True while the connection has stopped answering and has not yet been given up on. */
  readonly unstable: boolean;
  /** The smoothed round trip, or null before this link has answered anything. */
  readonly rttMs: number | null;
}

export function qualityOf(signals: QualitySignals): LinkQuality {
  return { path: pathOf(signals), band: bandOf(signals.rttMs) };
}

/** The same reading, from a peer the transport is reporting and a round trip it measured. */
export function qualityOfPeer(
  peer: TransportPeer,
  unstable: boolean,
  rttMs: number | null,
): LinkQuality {
  return qualityOf({
    snapshotChannelReady: peer.snapshotChannelReady,
    relayed: peer.relayed,
    unstable,
    rttMs,
  });
}

/**
 * Whether two readings say the same thing.
 *
 * A reading is a pair of words rather than one, so whoever decides that a roster is worth sending
 * to four machines cannot compare it with `!==` any more and get an answer that means anything.
 */
export function qualityEqual(left: LinkQuality, right: LinkQuality): boolean {
  return left.path === right.path && left.band === right.band;
}

/** What a player list renders for the path. The only place these words are written. */
export function pathLabel(path: LinkPath): string {
  return LABELS[path];
}

function pathOf(signals: QualitySignals): LinkPath {
  if (signals.unstable || !signals.snapshotChannelReady) return 'weak';
  if (signals.relayed === null) return 'unknown';
  return signals.relayed ? 'relayed' : 'direct';
}
